import { useState, useEffect, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'depreciation-tour-completed';

export function useDepreciationTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(completed === 'true');
  }, []);

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="depreciation-header"]',
      title: 'Welcome to Depreciation',
      content: 'This is where you calculate and process depreciation for your assets. Run depreciation batches for specific periods to keep your asset values up to date.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="current-period"]',
      title: 'Current Period',
      content: 'View the current financial period information. Depreciation is typically calculated on a monthly or yearly basis depending on your organization\'s settings.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="start-batch-button"]',
      title: 'Start New Depreciation Run',
      content: 'Click here to begin a new depreciation batch. You\'ll select assets and the system will calculate depreciation based on each asset\'s method and rate.',
      placement: 'left',
    },
    {
      target: '[data-tour="view-summary-button"]',
      title: 'View Last Batch Summary',
      content: 'Review the details and results of your most recently completed depreciation batch, including total depreciation calculated.',
      placement: 'left',
    },
    {
      target: '[data-tour="search-transactions"]',
      title: 'Search Transactions',
      content: 'Search through your depreciation transaction history to find specific batches by reference, date, or period.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="transaction-history"]',
      title: 'Transaction History',
      content: 'View all your depreciation batches with details like period, date of run, total depreciation, and status. Click any batch to view full details.',
      placement: 'top',
    },
    {
      target: '[data-tour="batch-calculations"]',
      title: 'Depreciation Calculations',
      content: 'Each batch shows the calculated depreciation amounts. The system automatically applies the correct depreciation method (straight-line, reducing balance, etc.) for each asset.',
      placement: 'left',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
